import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  SkipForward,
  HelpCircle,
  AlertTriangle,
  MousePointerClick,
  Gift,
  PauseCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/features/pricing/controller/useSubscription';
import type { OnboardingStep, SpotlightPosition } from '../model/types';
import {
  CENTERED_MAX_HEIGHT,
  SHEET_BOTTOM,
  SHEET_MAX_HEIGHT,
  SHEET_TOP,
  canHover,
  useIsPhoneViewport,
  useSheetOffsets,
} from './viewport';

interface OnboardingTooltipProps {
  step: OnboardingStep;
  position: SpotlightPosition | null;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  /** "Reprendre plus tard": closes without quitting; replaces the "Passer" button when given. */
  onPause?: () => void;
  onComplete: () => void;
  isFirst: boolean;
  isLast: boolean;
  isHovered: boolean;
  currentIndex: number;
  totalSteps: number;
  elementFound: boolean;
  requiresNavigation: boolean;
  isOnRequiredRoute: boolean;
}

const TOOLTIP_SPACING = 16;
const TOOLTIP_MARGIN = 16;
const PHONE_SIDE_MARGIN = 8;

// A closed dialog may stay mounted, hidden (the mobile sidebar drawer of every dashboard page).
function hasOpenDialog(tooltip: HTMLElement | null): boolean {
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
    (dialog) => !dialog.closest('[aria-hidden="true"]') && !tooltip?.contains(dialog) && !dialog.contains(tooltip),
  );
}

export const OnboardingTooltip = ({
  step,
  position,
  onNext,
  onPrev,
  onSkip,
  onPause,
  onComplete,
  isFirst,
  isLast,
  isHovered,
  currentIndex,
  totalSteps,
  elementFound,
  requiresNavigation,
  isOnRequiredRoute,
}: OnboardingTooltipProps) => {
  const { t } = useTranslation('onboarding');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const [tooltipHeight, setTooltipHeight] = useState(0);
  const isPhone = useIsPhoneViewport();
  const sheetOffsets = useSheetOffsets(isPhone, step.id);

  // Dernier message du tuto : rappeler les 3 matchs de test aux comptes sans
  // abonnement (plan STARTER). La requête n'est active que sur cette étape.
  const isFinalStep = step.id === 'coach-end';
  const { data: subscription } = useSubscription({ enabled: isFinalStep });
  const showTestMatchesNote = isFinalStep && subscription?.plan === 'STARTER';

  useEffect(() => {
    if (!tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    if (!position || step.placement === 'center') {
      // Centred by auto margins, not a transform: the entry animation's own transform would
      // throw the bubble off centre (half off a phone screen) while it plays. Longhands only: React
      // dropping an `inset` shorthand later would wipe the left/right set by the next style.
      setTooltipStyle({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: 'auto',
        height: 'fit-content',
        maxHeight: CENTERED_MAX_HEIGHT,
      });
      return;
    }

    // No room beside a target on a phone: full-width panel at the bottom, or at the top
    // when the target sits where the bottom panel would cover it.
    if (isPhone) {
      const sheetBottom = window.innerHeight - sheetOffsets.bottom;
      const sheetTop = sheetBottom - tooltipRect.height;
      const topSheetBottom = sheetOffsets.top + tooltipRect.height;
      // A tab of the bottom bar sits below the panel, not under it.
      const coveredAtBottom = position.top + position.height > sheetTop && position.top < sheetBottom;
      const dockTop = coveredAtBottom && position.top >= topSheetBottom;
      setTooltipStyle({
        left: PHONE_SIDE_MARGIN,
        right: PHONE_SIDE_MARGIN,
        width: 'auto',
        maxHeight: SHEET_MAX_HEIGHT,
        ...(dockTop ? { top: SHEET_TOP } : { bottom: SHEET_BOTTOM }),
      });
      return;
    }

    let top = 0;
    let left = 0;

    switch (step.placement) {
      case 'top':
        top = position.top - tooltipRect.height - TOOLTIP_SPACING;
        left = position.left + position.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = position.top + position.height + TOOLTIP_SPACING;
        left = position.left + position.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = position.top + position.height / 2 - tooltipRect.height / 2;
        left = position.left - tooltipRect.width - TOOLTIP_SPACING;
        break;
      case 'right':
        top = position.top + position.height / 2 - tooltipRect.height / 2;
        left = position.left + position.width + TOOLTIP_SPACING;
        break;
    }

    top = Math.max(
      TOOLTIP_MARGIN,
      Math.min(top, window.innerHeight - tooltipRect.height - TOOLTIP_MARGIN),
    );
    left = Math.max(
      TOOLTIP_MARGIN,
      Math.min(left, window.innerWidth - tooltipRect.width - TOOLTIP_MARGIN),
    );

    setTooltipStyle({ top, left, maxHeight: `calc(100dvh - ${TOOLTIP_MARGIN * 2}px)` });
  }, [position, step.placement, tooltipHeight, isPhone, sheetOffsets]);

  // The bubble grows (hint shown, buttons wrapping on a phone, longer German text): re-place it so
  // it never overflows the bottom of the screen.
  useEffect(() => {
    const node = tooltipRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setTooltipHeight(node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keyboard and screen-reader users land in the bubble, not behind the backdrop.
  useEffect(() => {
    tooltipRef.current?.focus({ preventScroll: true });
  }, [step.id]);

  // Escape only sets the tour aside ("Plus tard"): it must never skip or quit it for good.
  // An open dialog owns Escape (it closes first), even when its own handler does not prevent it.
  useEffect(() => {
    if (!onPause) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (hasOpenDialog(tooltipRef.current)) return;
      event.preventDefault();
      onPause();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onPause]);

  const canProceed = useMemo(() => {
    if (requiresNavigation) {
      return isOnRequiredRoute;
    }
    if (!elementFound) return true;
    if (step.requiredHover && !isHovered && canHover()) return false;
    return true;
  }, [elementFound, requiresNavigation, isOnRequiredRoute, step.requiredHover, isHovered]);

  return (
    <div
      ref={tooltipRef}
      tabIndex={-1}
      className="fixed z-[10002] w-[min(360px,calc(100vw-2rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-x-hidden overflow-y-auto overscroll-contain animate-in fade-in slide-in-from-bottom-4 duration-300 motion-reduce:animate-none focus:outline-none"
      style={tooltipStyle}
    >
      <div className="bg-gradient-to-r from-brand-800 to-brand-600 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-1.5 rounded-lg" aria-hidden="true">
              <HelpCircle size={18} className="text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium">
              {t('tooltip.step_of', { current: currentIndex + 1, total: totalSteps })}
            </span>
          </div>
          <button
            type="button"
            onClick={onSkip}
            className="flex h-11 w-11 items-center justify-center -mr-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors focus-visible:ring-2 focus-visible:ring-white/60"
            aria-label={onPause ? t('tooltip.quit_aria') : t('tooltip.skip_aria')}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <h3 className="text-white font-bold text-lg mt-2">{t(step.titleKey)}</h3>
      </div>

      <div className="px-4 py-3 sm:px-5 sm:py-4">
        <p className="text-slate-700 text-base sm:text-sm leading-relaxed">{t(step.descriptionKey)}</p>

        {showTestMatchesNote && (
          <div
            role="status"
            className="mt-4 flex items-start gap-2 text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg text-sm"
          >
            <Gift size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{t('tooltip.test_matches_note')}</span>
          </div>
        )}

        {!elementFound && (
          <div
            role="status"
            className="mt-4 flex items-center gap-2 text-action bg-action-light px-3 py-2 rounded-lg text-sm"
          >
            <HelpCircle size={16} aria-hidden="true" />
            <span>{t('tooltip.element_not_visible')}</span>
          </div>
        )}

        {elementFound && requiresNavigation && !isOnRequiredRoute && (
          <div
            role="status"
            className="mt-4 flex items-center gap-2 text-action bg-action-light px-3 py-2 rounded-lg text-sm"
          >
            <MousePointerClick size={16} aria-hidden="true" />
            <span>{t(canHover() ? 'tooltip.click_to_navigate' : 'tooltip.tap_to_navigate')}</span>
          </div>
        )}

        {elementFound && step.requiredHover && !isHovered && canHover() && !requiresNavigation && (
          <div
            role="status"
            className="mt-4 flex items-center gap-2 text-amber-800 bg-amber-50 px-3 py-2 rounded-lg text-sm"
          >
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{t('tooltip.hover_required')}</span>
          </div>
        )}
      </div>

      <div className="px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={isFirst}
          className={`flex min-h-11 min-w-11 items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-brand-400 ${
            isFirst
              ? 'text-slate-300 cursor-not-allowed'
              : 'text-slate-600 hover:bg-slate-200'
          }`}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          {/* Icon only on a phone, so the three buttons share one row and the panel stays short. */}
          <span className="sr-only sm:not-sr-only">{t('actions.previous')}</span>
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPause ?? onSkip}
            className="flex min-h-11 items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            {onPause ? <PauseCircle size={16} aria-hidden="true" /> : <SkipForward size={16} aria-hidden="true" />}
            {onPause ? t('actions.later') : t('actions.skip')}
          </button>

          {isLast ? (
            <button
              type="button"
              onClick={onComplete}
              disabled={!canProceed}
              className={`flex min-h-11 items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold transition-all focus-visible:ring-2 focus-visible:ring-offset-2 ${
                canProceed
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200 focus-visible:ring-emerald-400'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Check size={16} aria-hidden="true" />
              {t('actions.finish')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              disabled={!canProceed}
              className={`flex min-h-11 items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold transition-all focus-visible:ring-2 focus-visible:ring-offset-2 ${
                canProceed
                  ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-200 focus-visible:ring-brand-400'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {t('actions.next')}
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="px-5 py-2 bg-slate-100 flex gap-1 justify-center" aria-hidden="true">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === currentIndex
                ? 'w-6 bg-brand-600'
                : i < currentIndex
                  ? 'w-1.5 bg-brand-300'
                  : 'w-1.5 bg-slate-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
